import { useState } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, List, ListItem, ListItemText, Chip, CircularProgress, Alert, Button, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { MoreVert as MoreVertIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { usePatient, usePatientTests } from '@/hooks/usePatients';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { format, formatDistanceToNow } from 'date-fns';

/**
 * PatientDetailsPanel - Display patient information and test history
 */
export function PatientDetailsPanel() {
  const selectedPatientId = useMeasurementWizardStore((s) => s.selectedPatientId);
  const setPatientPanelMode = useMeasurementWizardStore((s) => s.setPatientPanelMode);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const { data: patient, isLoading: patientLoading, isError: patientError } = usePatient(selectedPatientId);
  const { data: patientHistory, isLoading: historyLoading } = usePatientTests(selectedPatientId);

  const handleChangePatient = () => {
    setPatientPanelMode('selection');
    setMenuAnchor(null);
  };

  const handleEditPatient = () => {
    setPatientPanelMode('edit');
    setMenuAnchor(null);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return format(new Date(date), 'MMMM d, yyyy');
  };

  const formatRelativeDate = (date: Date) => {
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return formatDistanceToNow(d, { addSuffix: true });
  };

  const getResultColor = (result: string): 'success' | 'warning' | 'error' | 'default' => {
    const lower = result.toLowerCase();
    if (lower === 'negative' || lower === 'normal') return 'success';
    if (lower === 'positive') return 'error';
    return 'warning';
  };

  if (patientLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (patientError || !patient) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={handleChangePatient}>
          Select Another
        </Button>
      }>
        Failed to load patient details
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Patient details</Typography>
        <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem onClick={handleEditPatient}>Edit patient</MenuItem>
          <MenuItem onClick={handleChangePatient}>Change patient</MenuItem>
        </Menu>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
        gap: 2,
        mb: 3,
      }}>
        {/* Full Name */}
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Full name
          </Typography>
          <Typography variant="body2">
            {patient.fullName}
          </Typography>
        </Box>

        {/* Date of Birth */}
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Date of birth
          </Typography>
          <Typography variant="body2">
            {formatDate(patient.dateOfBirth)}
          </Typography>
        </Box>

        {/* Sex */}
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Sex
          </Typography>
          <Typography variant="body2">
            {patient.sex}
          </Typography>
        </Box>
      </Box>

      <Accordion
        expanded={historyExpanded}
        onChange={() => setHistoryExpanded(!historyExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Patient history</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {historyLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!historyLoading && patientHistory && patientHistory.length > 0 && (
            <List disablePadding>
              {patientHistory.map((test) => (
                <ListItem key={test.id} disablePadding sx={{ mb: 1 }}>
                  <ListItemText
                    primary={test.profileName}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={test.result}
                          size="small"
                          color={getResultColor(test.result)}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatRelativeDate(test.completedAt)}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {!historyLoading && patientHistory && patientHistory.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No previous tests
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}